import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { employeeService } from '../services/service';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';
import type { ScheduleRuleRow, ScheduleRuleRowValidationErrors, SelectOption, UseFormAssignScheduleRule, UseFormAssignScheduleRuleProps } from '../types/hooks';
import type { Alert, SchedulingRuleAssignmentPayload, SchedulingRuleAssignmentUpdatePayload } from '../types/services';

const INDEFINITE_DATE = dayjs('3000-01-01');

const emptyRowErrors: ScheduleRuleRowValidationErrors = {
    scheduleRuleId: '',
    startDate: '',
    endDate: '',
};

const getRowKey = (row: ScheduleRuleRow) => `${row.employeeScheduleRuleId ?? 'new'}-${row.scheduleRuleId}-${row.startDate?.format('YYYY-MM-DD') ?? ''}-${row.endDate?.format('YYYY-MM-DD') ?? ''}`;

const sortRowsByDate = (rows: ScheduleRuleRow[]) =>
    [...rows].sort((first, second) => (first.startDate?.valueOf() ?? Number.MAX_SAFE_INTEGER) - (second.startDate?.valueOf() ?? Number.MAX_SAFE_INTEGER));

const getAssignmentId = (assignment: { ruleset_r_employee_id?: number; employee_schedule_rule_id?: number; id?: number }) =>
    assignment.ruleset_r_employee_id ?? assignment.employee_schedule_rule_id ?? assignment.id ?? null;

const useFormAssignScheduleRule = ({ rowsSelected, option, setOption, reload }: UseFormAssignScheduleRuleProps): UseFormAssignScheduleRule => {
    const { t } = useTranslation('workers.form.assign_schedule_rule');
    const { showAlert, closeAlert } = useRbSystemAlert();
    const canAssignScheduleRule = usePermiso('asignar-regla-horario');
    const idCounter = useRef(0);

    const [openForm, setOpenForm] = useState(false);
    const [ruleOptions, setRuleOptions] = useState<SelectOption[]>([]);
    const [rows, setRows] = useState<ScheduleRuleRow[]>([]);
    const [initialRows, setInitialRows] = useState<ScheduleRuleRow[]>([]);
    const [deletedAssignmentIds, setDeletedAssignmentIds] = useState<number[]>([]);
    const [searchRule, setSearchRule] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<number, ScheduleRuleRowValidationErrors>>({});

    const selectedEmployee = rowsSelected[0] ?? null;
    const selectedWorkerLabel = selectedEmployee ? `${selectedEmployee.person_number} - ${selectedEmployee.fullname}` : '';

    const alert: Alert = {
        t,
        showAlert: (severity, message) => showAlert(severity, message),
        closeAlert,
    };

    const buildNewRow = (): ScheduleRuleRow => {
        idCounter.current += 1;
        return {
            id: idCounter.current,
            employeeScheduleRuleId: null,
            employeeId: selectedEmployee?.id ?? '',
            scheduleRuleId: '',
            startDate: null,
            endDate: null,
        };
    };

    const resetForm = () => {
        setRows([]);
        setInitialRows([]);
        setDeletedAssignmentIds([]);
        setRuleOptions([]);
        setSearchRule('');
        setValidationMessage('');
        setValidationErrors({});
        setShowMessage(false);
        idCounter.current = 0;
    };

    const closeForm = () => {
        setOpenForm(false);
        setOption(null);
        resetForm();
    };

    const hasChanges = () => deletedAssignmentIds.length > 0 || JSON.stringify(rows.map(getRowKey)) !== JSON.stringify(initialRows.map(getRowKey));

    const handleCancel = () => {
        if (hasChanges()) {
            setShowMessage(true);
            return;
        }

        closeForm();
    };

    const loadData = async () => {
        if (!selectedEmployee) return;

        setLoadingData(true);
        const [{ rules }, { assignments }] = await Promise.all([
            employeeService.getScheduleRules({ alert }),
            employeeService.getEmployeeScheduleRules({
                employeeId: selectedEmployee.id,
                alert,
            }),
        ]);

        setRuleOptions(rules);

        const loadedRows = assignments.map((assignment, index) => ({
            id: index + 1,
            employeeScheduleRuleId: getAssignmentId(assignment),
            employeeId: String(assignment.employee_id),
            scheduleRuleId: String(assignment.ruleset_id ?? assignment.scheduling_rule_id ?? ''),
            startDate: dayjs(assignment.start_date),
            endDate: dayjs(assignment.end_date),
        }));

        idCounter.current = loadedRows.length;
        const nextRows = sortRowsByDate(loadedRows);

        setRows(nextRows);
        setInitialRows(nextRows);
        setDeletedAssignmentIds([]);
        validateRows(nextRows);
        setLoadingData(false);
    };

    const addRow = () => {
        setRows((currentRows) => {
            const updatedRows = [...currentRows, buildNewRow()];
            validateRows(updatedRows);
            return updatedRows;
        });
    };

    const removeRow = (rowId: number) => {
        setRows((currentRows) => {
            const rowToRemove = currentRows.find((row) => row.id === rowId);
            if (rowToRemove?.employeeScheduleRuleId) {
                setDeletedAssignmentIds((currentIds) => [...currentIds, rowToRemove.employeeScheduleRuleId!]);
            }

            const updatedRows = currentRows.filter((row) => row.id !== rowId);
            validateRows(updatedRows);
            return updatedRows;
        });
    };

    const updateRow = (rowId: number, changes: Partial<Omit<ScheduleRuleRow, 'id' | 'employeeId'>>) => {
        setRows((currentRows) => {
            const updatedRows = currentRows.map((row) => (row.id === rowId ? { ...row, ...changes } : row));
            const sortedRows = changes.startDate !== undefined ? sortRowsByDate(updatedRows) : updatedRows;
            validateRows(sortedRows);
            return sortedRows;
        });
    };

    const getValidationErrors = (targetRows: ScheduleRuleRow[]) => {
        const nextErrors: Record<number, ScheduleRuleRowValidationErrors> = {};

        targetRows.forEach((row, index) => {
            const rowErrors: ScheduleRuleRowValidationErrors = { ...emptyRowErrors };

            if (!row.scheduleRuleId) {
                rowErrors.scheduleRuleId = t('validation.rule_required');
            }

            if (!row.startDate?.isValid()) {
                rowErrors.startDate = t('validation.start_date_required');
            }

            if (!row.endDate?.isValid()) {
                rowErrors.endDate = t('validation.end_date_required');
            }

            if (row.startDate?.isValid() && row.endDate?.isValid()) {
                if (!row.endDate.isAfter(row.startDate, 'day')) {
                    rowErrors.endDate = t('validation.date_range_invalid');
                }

                const hasIndefinite = targetRows.some((currentRow, currentIndex) => currentIndex !== index && Boolean(currentRow.endDate && currentRow.endDate.year() >= INDEFINITE_DATE.year()));

                if (hasIndefinite && row.endDate.isSame(INDEFINITE_DATE, 'day')) {
                    rowErrors.endDate = t('validation.indefinite_exists');
                }

                const overlappingRow = targetRows.find((currentRow, currentIndex) => {
                    if (currentIndex === index || !currentRow.startDate?.isValid() || !currentRow.endDate?.isValid() || !row.startDate || !row.endDate) {
                        return false;
                    }

                    return !row.startDate.isAfter(currentRow.endDate, 'day') && !row.endDate.isBefore(currentRow.startDate, 'day');
                });

                if (overlappingRow) {
                    rowErrors.endDate = t('validation.date_overlap');
                }
            }

            if (Object.values(rowErrors).some(Boolean)) {
                nextErrors[row.id] = rowErrors;
            }
        });

        return nextErrors;
    };

    const validateRows = (targetRows: ScheduleRuleRow[] = rows) => {
        const nextErrors = getValidationErrors(targetRows);
        setValidationErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const canSubmit = Boolean(!loading && !loadingData && Object.keys(getValidationErrors(rows)).length === 0);

    const submit = async () => {
        if (!canAssignScheduleRule || !selectedEmployee) return;

        if (!validateRows()) {
            setValidationMessage(t('alerts.validation_error'));
            return;
        }

        const payload = rows.map((row) => ({
            assignmentId: row.employeeScheduleRuleId,
            body: {
                employee_id: Number(row.employeeId),
                ruleset_id: Number(row.scheduleRuleId),
                start_date: row.startDate!.format('YYYY-MM-DD'),
                end_date: row.endDate!.format('YYYY-MM-DD'),
            },
        }));

        const toCreate: SchedulingRuleAssignmentPayload[] = payload.filter((assignment) => assignment.assignmentId === null).map((assignment) => assignment.body);

        const toUpdate = payload.filter((assignment): assignment is { assignmentId: number; body: SchedulingRuleAssignmentPayload } => assignment.assignmentId !== null);

        setLoading(true);
        const results = await Promise.all([
            toCreate.length > 0 ? employeeService.createEmployeeScheduleRules({ body: toCreate, alert }) : Promise.resolve(true),
            ...toUpdate.map((assignment) =>
                employeeService.updateEmployeeScheduleRule({
                    assignmentId: assignment.assignmentId,
                    body: {
                        ...assignment.body,
                        ruleset_r_employee_id: assignment.assignmentId,
                    } as SchedulingRuleAssignmentUpdatePayload,
                    alert,
                }),
            ),
            ...deletedAssignmentIds.map((assignmentId) => employeeService.deleteEmployeeScheduleRule({ assignmentId, alert })),
        ]);

        if (results.every(Boolean)) {
            showAlert('success', t('alerts.success'));
            await reload();
            closeForm();
        }

        setLoading(false);
    };

    useEffect(() => {
        if (option !== 'assign_schedule_rule') return;

        if (!canAssignScheduleRule) {
            setOption(null);
            return;
        }

        if (rowsSelected.length !== 1) {
            setValidationMessage(t('validation.one_worker'));
            setOption(null);
            return;
        }

        setOpenForm(true);
        setOption(null);
    }, [option, rowsSelected.length, setOption, t, canAssignScheduleRule]);

    useEffect(() => {
        if (openForm) {
            loadData();
        }
    }, [openForm]);

    return {
        t,
        title: t('title'),
        openForm,
        rowsSelected,
        selectedWorkerLabel,
        ruleOptions,
        rows,
        searchRule,
        setSearchRule,
        loading,
        loadingData,
        validationMessage,
        setValidationMessage,
        showMessage,
        setShowMessage,
        validationErrors,
        canSubmit,
        hasChanges,
        closeForm,
        handleCancel,
        addRow,
        removeRow,
        updateRow,
        submit,
    };
};

export { useFormAssignScheduleRule };
